import { OfflineIndicator } from '@/components/offline/OfflineIndicator';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { execute } from '@/graphql/execute';
import { cn } from '@/lib/utils';
import { layersQuery } from '@/queries/layer.query';
import { getProjectQuery } from '@/queries/project.query';
import {
  deleteProjectFolderMutation,
  moveProjectToFolderMutation,
  projectFoldersQuery,
  renameProjectFolderMutation,
} from '@/queries/projectFolder.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderClosed, FolderOpen, Pencil, Plus, Trash2 } from 'lucide-react';
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useIsActive } from '.';

const SCROLL_ZONE = 40; // px from edge to trigger scroll
const SCROLL_SPEED = 8; // px per frame
const PROJECT_DRAG_MIME = 'application/x-skygis-project-id';
const PROJECT_DRAG_TEXT_PREFIX = 'skygis-project:';

const setDraggedProjectId = (dataTransfer: DataTransfer, projectId: string) => {
  dataTransfer.effectAllowed = 'move';
  dataTransfer.setData(PROJECT_DRAG_MIME, projectId);
  // Some browsers / embedded webviews are much more reliable with text/plain
  // than with custom MIME types. Keep a prefixed fallback so external text
  // drops are ignored.
  dataTransfer.setData('text/plain', `${PROJECT_DRAG_TEXT_PREFIX}${projectId}`);
};

const getDroppedProjectId = (dataTransfer: DataTransfer, fallbackProjectId: string | null) => {
  const customValue = dataTransfer.getData(PROJECT_DRAG_MIME);
  if (customValue) return customValue;

  const plainValue = dataTransfer.getData('text/plain');
  if (plainValue.startsWith(PROJECT_DRAG_TEXT_PREFIX)) return plainValue.slice(PROJECT_DRAG_TEXT_PREFIX.length);

  return fallbackProjectId;
};

/** Auto-scrolls the nearest scrollable ancestor when dragging near its edges. */
function useAutoScrollOnDrag() {
  const rafId = useRef<number>(0);
  const scrollContainer = useRef<HTMLElement | null>(null);

  const findScrollParent = useCallback((el: HTMLElement | null): HTMLElement | null => {
    while (el) {
      const { overflowY } = getComputedStyle(el);
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return null;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!scrollContainer.current) return;
    const container = scrollContainer.current;
    const rect = container.getBoundingClientRect();
    const y = e.clientY;

    cancelAnimationFrame(rafId.current);

    if (y - rect.top < SCROLL_ZONE) {
      // Near top edge — scroll up
      const intensity = 1 - (y - rect.top) / SCROLL_ZONE;
      const step = () => {
        container.scrollTop -= SCROLL_SPEED * intensity;
        rafId.current = requestAnimationFrame(step);
      };
      rafId.current = requestAnimationFrame(step);
    } else if (rect.bottom - y < SCROLL_ZONE) {
      // Near bottom edge — scroll down
      const intensity = 1 - (rect.bottom - y) / SCROLL_ZONE;
      const step = () => {
        container.scrollTop += SCROLL_SPEED * intensity;
        rafId.current = requestAnimationFrame(step);
      };
      rafId.current = requestAnimationFrame(step);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    cancelAnimationFrame(rafId.current);
  }, []);

  const rootRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainer.current = node ? findScrollParent(node) : null;
    },
    [findScrollParent],
  );

  return { rootRef, handleDragOver, handleDragEnd };
}

type Project = {
  id: string;
  name: string;
  srid: number;
  organizationId: string;
  folderId: string | null | undefined;
  storageUsedBytes: number;
};

type Folder = {
  id: string;
  name: string;
};

interface ProjectListProps {
  projects: Project[];
  organizationId: string;
  onCreateProject: (folderId?: string) => void;
  onboardingProjectId?: string | null;
}

export const ProjectList: FunctionComponent<ProjectListProps> = ({
  projects,
  organizationId,
  onCreateProject,
  onboardingProjectId,
}) => {
  const { t } = useTranslation('sidebar');
  const queryClient = useQueryClient();
  const { rootRef, handleDragOver: handleAutoScroll, handleDragEnd } = useAutoScrollOnDrag();
  const [draggedProjectId, setDraggedProjectIdState] = useState<string | null>(null);

  const { data: foldersData } = useQuery({
    queryKey: ['projectFolders', organizationId],
    queryFn: () => execute(projectFoldersQuery),
  });

  const folders = foldersData?.projectFolders || [];

  const renameFolder = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => execute(renameProjectFolderMutation, { id, name }),
    onMutate: async ({ id, name }) => {
      await queryClient.cancelQueries({ queryKey: ['projectFolders', organizationId] });
      const prev = queryClient.getQueryData(['projectFolders', organizationId]);
      queryClient.setQueryData(['projectFolders', organizationId], (old: any) => {
        if (!old?.projectFolders) return old;
        return {
          ...old,
          projectFolders: old.projectFolders.map((f: Folder) => (f.id === id ? { ...f, name } : f)),
        };
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['projectFolders', organizationId], context.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['projectFolders', organizationId] });
    },
  });

  const deleteFolder = useMutation({
    mutationFn: (id: string) => execute(deleteProjectFolderMutation, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectFolders', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['projects', organizationId] });
    },
  });

  const moveProject = useMutation({
    mutationFn: ({ projectId, folderId }: { projectId: string; folderId: string | null }) =>
      execute(moveProjectToFolderMutation, { projectId, folderId }),
    onMutate: async ({ projectId, folderId }) => {
      await queryClient.cancelQueries({ queryKey: ['projects', organizationId] });
      const prev = queryClient.getQueryData(['projects', organizationId]);
      queryClient.setQueryData(['projects', organizationId], (old: any) => {
        if (!old?.projects) return old;
        return {
          ...old,
          projects: old.projects.map((p: Project) => (p.id === projectId ? { ...p, folderId } : p)),
        };
      });
      return { prev };
    },
    onSuccess: (data) => {
      const movedProject = data.moveProjectToFolder;
      queryClient.setQueryData(['projects', organizationId], (old: any) => {
        if (!old?.projects) return old;
        return {
          ...old,
          projects: old.projects.map((p: Project) =>
            p.id === movedProject.id ? { ...p, folderId: movedProject.folderId } : p,
          ),
        };
      });
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['projects', organizationId], context.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', organizationId] });
    },
  });

  const _handleRenameFolder = useCallback(
    (id: string, name: string) => {
      renameFolder.mutate({ id, name });
    },
    [renameFolder],
  );

  const _handleDeleteFolder = useCallback(
    (id: string) => {
      deleteFolder.mutate(id);
    },
    [deleteFolder],
  );

  const _handleMoveProject = useCallback(
    (projectId: string, folderId: string | null) => {
      moveProject.mutate({ projectId, folderId });
    },
    [moveProject],
  );

  // Group projects: unfoldered first, then by folder
  const unfolderedProjects = projects.filter((p) => !p.folderId).sort((a, b) => a.name.localeCompare(b.name));

  const sortedFolders = [...folders].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div ref={rootRef} onDragOver={handleAutoScroll} onDragEnd={handleDragEnd} onDrop={handleDragEnd}>
      {/* Folders always on top */}
      {sortedFolders.map((folder) => {
        const folderProjects = projects
          .filter((p) => p.folderId === folder.id)
          .sort((a, b) => a.name.localeCompare(b.name));

        return (
          <ProjectFolder
            key={folder.id}
            folder={folder}
            projects={folderProjects}
            onboardingProjectId={onboardingProjectId}
            onRename={_handleRenameFolder}
            onDelete={_handleDeleteFolder}
            onDropProject={(projectId) => _handleMoveProject(projectId, folder.id)}
            onCreateProject={() => onCreateProject(folder.id)}
            draggedProjectId={draggedProjectId}
            onProjectDragStart={setDraggedProjectIdState}
            onProjectDragEnd={() => setDraggedProjectIdState(null)}
          />
        );
      })}

      {/* Unfoldered projects below folders - drop zone for "remove from folder" */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        }}
        onDrop={(e) => {
          e.preventDefault();
          const projectId = getDroppedProjectId(e.dataTransfer, draggedProjectId);
          if (projectId) _handleMoveProject(projectId, null);
          setDraggedProjectIdState(null);
        }}
      >
        {unfolderedProjects.map((project) => (
          <DraggableProject
            key={project.id}
            project={project}
            isOnboardingProject={project.id === onboardingProjectId}
            onProjectDragStart={setDraggedProjectIdState}
            onProjectDragEnd={() => setDraggedProjectIdState(null)}
          />
        ))}
      </div>
    </div>
  );
};

/** A draggable project item */
const DraggableProject: FunctionComponent<{
  project: Project;
  isOnboardingProject?: boolean;
  onProjectDragStart?: (projectId: string) => void;
  onProjectDragEnd?: () => void;
}> = ({ project, isOnboardingProject = false, onProjectDragStart, onProjectDragEnd }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isActive = useIsActive(`/projects/${project.id}`, true);
  const prefetchTimeout = useRef<ReturnType<typeof setTimeout>>(null);
  const suppressClickUntil = useRef(0);

  const _handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (Date.now() < suppressClickUntil.current) {
        return;
      }
      if (e.metaKey || e.ctrlKey) window.open(`/projects/${project.id}`, '_blank');
      else navigate(`/projects/${project.id}`);
    },
    [navigate, project.id],
  );

  const _handleMouseEnter = useCallback(() => {
    prefetchTimeout.current = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: ['project', project.id],
        queryFn: () => execute(getProjectQuery, { id: project.id }),
        staleTime: 30_000,
      });
      queryClient.prefetchQuery({
        queryKey: ['layers', project.id],
        queryFn: () => execute(layersQuery, { projectId: project.id }),
        staleTime: 30_000,
      });
    }, 50);
  }, [project.id, queryClient]);

  return (
    <button
      id={isOnboardingProject ? 'onboarding-first-project' : undefined}
      draggable
      onDragStart={(e) => {
        suppressClickUntil.current = Date.now() + 750;
        setDraggedProjectId(e.dataTransfer, project.id);
        onProjectDragStart?.(project.id);
      }}
      onDragEnd={() => {
        suppressClickUntil.current = Date.now() + 250;
        onProjectDragEnd?.();
      }}
      onClick={_handleClick}
      onMouseEnter={_handleMouseEnter}
      onMouseLeave={() => prefetchTimeout.current && clearTimeout(prefetchTimeout.current)}
      className={cn(
        'flex w-full relative items-center font-medium gap-2 border border-transparent overflow-hidden rounded-md px-2.5 pr-2 text-left outline-none ring-sidebar-ring focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 text-slate-700/90 dark:text-stone-200 h-8 text-sm [&>svg]:shrink-0 [&>svg]:size-4 [&>svg]:stroke-2 cursor-grab active:cursor-grabbing',
        isActive
          ? 'bg-white dark:bg-stone-900 border-slate-200 dark:border-stone-800 text-slate-900 shadow-sm/3'
          : 'active:bg-slate-200/50 active:text-slate-700 hover:bg-slate-200/25 dark:active:bg-stone-800/50 dark:active:text-stone-200 dark:hover:bg-stone-800/25',
      )}
    >
      <span className='flex-1 font-[450] truncate text-left'>{project.name}</span>
      <OfflineIndicator projectId={project.id} />
    </button>
  );
};

/** A folder that contains projects, supports drag-and-drop */
const ProjectFolder: FunctionComponent<{
  folder: Folder;
  projects: Project[];
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onDropProject: (projectId: string) => void;
  onCreateProject: () => void;
  draggedProjectId: string | null;
  onProjectDragStart: (projectId: string) => void;
  onProjectDragEnd: () => void;
  onboardingProjectId?: string | null;
}> = ({
  folder,
  projects,
  onRename,
  onDelete,
  onDropProject,
  onCreateProject,
  draggedProjectId,
  onProjectDragStart,
  onProjectDragEnd,
  onboardingProjectId,
}) => {
  const { t } = useTranslation('sidebar');
  const [isOpen, setIsOpen] = useState(() => {
    return localStorage.getItem(`folder-${folder.id}`) !== 'false';
  });
  const [isDragOver, setIsDragOver] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(folder.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const expandTimer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (!isRenaming) return;
    // Defer so Radix context menu finishes restoring focus before we grab it
    const raf = requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(0, inputRef.current.value.length);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [isRenaming]);

  useEffect(() => {
    if (!onboardingProjectId || !projects.some((project) => project.id === onboardingProjectId)) return;
    setIsOpen(true);
    localStorage.setItem(`folder-${folder.id}`, 'true');
  }, [folder.id, onboardingProjectId, projects]);

  const _handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      localStorage.setItem(`folder-${folder.id}`, next.toString());
      return next;
    });
  }, [folder.id]);

  const _handleRenameSubmit = useCallback(() => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== folder.name) {
      onRename(folder.id, trimmed);
    } else {
      setRenameValue(folder.name);
    }
    setIsRenaming(false);
  }, [renameValue, folder.name, folder.id, onRename]);

  const _handleDelete = useCallback(() => {
    onDelete(folder.id);
    toast.success(t('folders.folderDeleted'));
  }, [folder.id, onDelete, t]);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          className={cn('rounded-md transition-colors', isDragOver && 'bg-blue-100/60 dark:bg-blue-900/30')}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setIsDragOver(true);
            // Auto-expand collapsed folders after hovering 500ms
            if (!isOpen && !expandTimer.current) {
              expandTimer.current = setTimeout(() => {
                setIsOpen(true);
                localStorage.setItem(`folder-${folder.id}`, 'true');
                expandTimer.current = null;
              }, 500);
            }
          }}
          onDragLeave={(e) => {
            // Only clear if leaving the actual container, not entering a child
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setIsDragOver(false);
              if (expandTimer.current) {
                clearTimeout(expandTimer.current);
                expandTimer.current = null;
              }
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            if (expandTimer.current) {
              clearTimeout(expandTimer.current);
              expandTimer.current = null;
            }
            const projectId = getDroppedProjectId(e.dataTransfer, draggedProjectId);
            if (projectId) {
              onDropProject(projectId);
              onProjectDragEnd();
              // Auto-expand when dropping
              if (!isOpen) {
                setIsOpen(true);
                localStorage.setItem(`folder-${folder.id}`, 'true');
              }
            }
          }}
        >
          {/* Folder header */}
          <button
            onClick={_handleToggle}
            onDoubleClick={(e) => {
              e.preventDefault();
              setRenameValue(folder.name);
              setIsRenaming(true);
            }}
            className={cn(
              'flex w-full font-medium items-center gap-1.5 overflow-hidden rounded-md px-2.5 pr-3.5 text-left outline-none h-8 text-sm [&>svg]:shrink-0 [&>svg]:size-3.5 cursor-pointer select-none',
              'text-slate-600 dark:text-stone-300 hover:bg-slate-200/25 active:bg-slate-200/50 dark:hover:bg-stone-800/25 dark:active:bg-stone-800/50',
            )}
          >
            {/* <ChevronRight
              className={cn('size-3 text-slate-400 dark:text-stone-500 transition-transform', isOpen && 'rotate-90')}
            /> */}
            {isOpen ? (
              <FolderOpen className='size-3.5 text-slate-400 dark:text-stone-100' />
            ) : (
              <FolderClosed className='size-3.5 text-slate-400 dark:text-stone-100' />
            )}
            {isRenaming ? (
              <input
                ref={inputRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={_handleRenameSubmit}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') _handleRenameSubmit();
                  if (e.key === 'Escape') {
                    setRenameValue(folder.name);
                    setIsRenaming(false);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className='flex-1 bg-transparent border-none outline-none text-sm font-medium text-slate-700 dark:text-stone-200 min-w-0'
              />
            ) : (
              <span className='flex-1 truncate font-[450]'>{folder.name}</span>
            )}
            {projects.length > 0 && (
              <span className='text-[10px] font-medium text-slate-400 dark:text-stone-500 tabular-nums'>
                {projects.length}
              </span>
            )}
          </button>

          {/* Folder contents */}
          {isOpen && (
            <div className='ml-4 pl-2.5 border-l border-slate-200 dark:border-stone-800'>
              {projects.map((project) => (
                <DraggableProject
                  key={project.id}
                  project={project}
                  isOnboardingProject={project.id === onboardingProjectId}
                  onProjectDragStart={onProjectDragStart}
                  onProjectDragEnd={onProjectDragEnd}
                />
              ))}
              {projects.length === 0 && (
                <div className='py-2 px-2.5 text-xs text-slate-400 dark:text-stone-500 italic'>
                  {t('folders.emptyFolder')}
                </div>
              )}
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent
        onCloseAutoFocus={(e) => {
          if (isRenaming) e.preventDefault();
        }}
      >
        <ContextMenuItem onClick={onCreateProject} className='gap-2 text-xs'>
          <Plus className='size-3.5' />
          {t('folders.createProject')}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          onClick={() => {
            setRenameValue(folder.name);
            setIsRenaming(true);
          }}
          className='gap-2 text-xs'
        >
          <Pencil className='size-3.5' />
          {t('folders.rename')}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem variant='destructive' onClick={_handleDelete} className='gap-2 text-xs'>
          <Trash2 className='size-3.5' />
          {t('folders.delete')}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};
